import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import { useCachedData } from '@/hooks/useCachedData'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'
import { showToast } from '@/components/layout/Toast'
import { useAuth } from '@/hooks/useAuth'
import { IconMessageCircle, IconSettings } from '@/components/icons/Icons'
import ChatSidebar from '@/components/chat/ChatSidebar'
import ChatWindow from '@/components/chat/ChatWindow'
import NewConversationModal from '@/components/chat/NewConversationModal'
import type { Conversacion, Contacto, CanalChat, Empresa } from '@/lib/types'

const CANAL_URL_TO_INTERNAL: Record<string, CanalChat> = {
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  webchat: 'web_chat',
  email: 'email',
  sms: 'sms',
  portal: 'portal',
}

const CANAL_INTERNAL_TO_URL: Record<CanalChat, string> = {
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  web_chat: 'webchat',
  email: 'email',
  sms: 'sms',
  portal: 'portal',
}

export default function Conversaciones() {
  const db = useSupabase()
  const navigate = useNavigate()
  /*
    La MISMA bandeja para los dos. Lo que cambia no es la pantalla sino de
    quién son los datos, y eso ya lo decide el backend: las conversaciones se
    scopean por `conversaciones.partner_id` y las credenciales de Wazzup se
    resuelven contra la cuenta del partner, así que él ve y responde por SU
    número.

    Acá el rol decide una sola cosa: el prefijo de las rutas. Las cuatro
    navegaciones iban a `/conversaciones/...` fijo, así que en su panel lo
    habrían sacado a una ruta de admin que su guard rechaza.
  */
  const { isPartner } = useAuth()
  const baseRuta = isPartner ? '/partner/conversaciones' : '/conversaciones'
  const { canal: canalParam, origen: origenParam, convId: convIdParam } = useParams<{
    canal?: string
    origen?: string
    convId?: string
  }>()


  const [filter, setFilter] = useState<string>('todas')
  const [searchQuery, setSearchQuery] = useState('')
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768 || isStandalone)
  const [showNewConvModal, setShowNewConvModal] = useState(false)
  const [contactDetailId, setContactDetailId] = useState<number | null>(null)

  const activeCanal: CanalChat | null = canalParam ? (CANAL_URL_TO_INTERNAL[canalParam] ?? null) : null
  const activeOrigen = origenParam || null
  const selectedId = convIdParam ? parseInt(convIdParam, 10) : null

  function isChannelVisible(canal: string, channelId: string | null): boolean {
    const cfg = canalesConfig[canal]
    if (!cfg) return true
    if (!cfg.visible) return false
    const originCfg = cfg.origins?.[channelId || 'default']
    return !originCfg || originCfg.visible
  }

  /* El Inbox en cache, pero con 30 segundos y no cinco minutos: es la pantalla
     mas en vivo del ERP. Lo que gana es el reingreso —volver de un chat a la
     lista muestra lo que ya tenia, sin el "Cargando" en blanco— y detras
     revalida. Lo de verdad nuevo no lo trae esta consulta sino el SSE, que
     retoca el cache con `actualizar()` igual que antes retocaba el useState. */
  const { data: datos, isLoading: loading, refrescar, actualizar } = useCachedData(
    ['inbox'],
    async () => {
      const [all, empresas] = await Promise.all([
        db.getAll<Conversacion>('conversaciones'),
        db.getAll<Empresa>('empresa'),
      ]).catch(() => {
        showToast('Error al cargar conversaciones', 'error')
        return [[], []] as [Conversacion[], Empresa[]]
      })
      all.sort((a, b) => {
        const ta = a.ultimo_mensaje_at ? new Date(a.ultimo_mensaje_at).getTime() : 0
        const tb = b.ultimo_mensaje_at ? new Date(b.ultimo_mensaje_at).getTime() : 0
        return tb - ta
      })
      const canalesConfig = (empresas.length > 0 && empresas[0].canales_config) || {}
      const contactIds = [...new Set(all.filter(c => c.contacto_id).map(c => c.contacto_id!))]
      const contactMap: Record<number, Contacto> = {}
      if (contactIds.length > 0) {
        const { data: contacts } = await supabase
          .from('contactos')
          .select('*')
          .in('id', contactIds)
        for (const c of contacts ?? []) contactMap[(c as Contacto).id] = c as Contacto
      }
      return { conversations: all, contactMap, canalesConfig }
    },
    { staleTime: 30 * 1000 },
  )
  const conversations = datos?.conversations ?? []
  const contactMap = datos?.contactMap ?? {}
  const canalesConfig = datos?.canalesConfig ?? {}
  const loadConversations = refrescar

  useRealtimeEvent('new_conversation', useCallback((data: any) => {
    const conv = data?.conversacion as Conversacion
    if (!conv) return
    actualizar(prev => (
      prev.conversations.some(c => c.id === conv.id)
        ? prev
        : { ...prev, conversations: [conv, ...prev.conversations] }
    ))
    if (conv.contacto_id && !contactMap[conv.contacto_id]) {
      supabase.from('contactos').select('*').eq('id', conv.contacto_id).then(({ data: contacts }) => {
        if (contacts?.[0]) {
          actualizar(prev => ({ ...prev, contactMap: { ...prev.contactMap, [conv.contacto_id!]: contacts[0] as Contacto } }))
        }
      })
    }
  }, [contactMap]))

  useRealtimeEvent('conversation_updated', useCallback((data: any) => {
    if (!data?.id) return
    actualizar(prev => ({
      ...prev,
      conversations: prev.conversations.map(c => c.id === data.id ? { ...c, ...data } : c)
        .sort((a, b) => {
          const ta = a.ultimo_mensaje_at ? new Date(a.ultimo_mensaje_at).getTime() : 0
          const tb = b.ultimo_mensaje_at ? new Date(b.ultimo_mensaje_at).getTime() : 0
          return tb - ta
        }),
    }))
  }, []))

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
    const handler = () => setIsMobile(window.innerWidth < 768 || standalone)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const handleSelect = async (id: number) => {
    const conv = conversations.find(c => c.id === id)
    if (!conv) return

    const canalSlug = CANAL_INTERNAL_TO_URL[conv.canal]
    const origen = conv.channel_id || 'default'
    navigate(`${baseRuta}/${canalSlug}/${origen}/${id}`, { replace: false })

    if (conv.mensajes_no_leidos > 0) {
      try {
        await db.update('conversaciones', { id, mensajes_no_leidos: 0 })
        actualizar(prev => ({
          ...prev,
          conversations: prev.conversations.map(c => c.id === id ? { ...c, mensajes_no_leidos: 0 } : c),
        }))
      } catch { /* non-critical */ }
    }
  }

  const handleBack = useCallback(() => {
    if (canalParam && origenParam) {
      navigate(`${baseRuta}/${canalParam}/${origenParam}`, { replace: true })
    } else {
      navigate('/conversaciones', { replace: true })
    }
  }, [canalParam, origenParam, navigate])

  useEffect(() => {
    if (!isMobile || !convIdParam) return
    const onPopState = () => {
      if (canalParam && origenParam) {
        navigate(`${baseRuta}/${canalParam}/${origenParam}`, { replace: true })
      } else {
        navigate('/conversaciones', { replace: true })
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [isMobile, convIdParam, canalParam, origenParam, navigate])

  const handleConversationStarted = async (convId: number) => {
    setShowNewConvModal(false)
    // De lo que DEVUELVE el refresco, no de `conversations`: esa variable es la
    // de este render y no tiene la conversacion recien creada, asi que el chat
    // no se abria y el modal se cerraba dejando al usuario en la lista.
    const r = await loadConversations()
    const conv = (r.data?.conversations ?? conversations).find(c => c.id === convId)
    if (conv) {
      const canalSlug = CANAL_INTERNAL_TO_URL[conv.canal]
      const origen = conv.channel_id || 'default'
      navigate(`${baseRuta}/${canalSlug}/${origen}/${convId}`, { replace: true })
    }
  }

  const originNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const [canal, cfg] of Object.entries(canalesConfig)) {
      if (cfg?.origins) {
        for (const [channelId, originCfg] of Object.entries(cfg.origins)) {
          if (originCfg.nombre) {
            map[`${canal}:${channelId}`] = originCfg.nombre
          }
        }
      }
    }
    return map
  }, [canalesConfig])

  const sidebarConversations = useMemo(() => {
    return conversations.filter(c => {
      if (c.chat_id) {
        if (/^(test-|marco-)/.test(c.chat_id)) return false
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(c.chat_id)) return false
      }
      if (!isChannelVisible(c.canal, c.channel_id)) return false
      if (filter === 'no_leidas' && c.mensajes_no_leidos === 0) return false
      if (filter === 'sin_vincular' && c.contacto_id !== null) return false
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase()
        const chatId = c.chat_id?.toLowerCase() || ''
        const preview = c.ultimo_mensaje?.toLowerCase() || ''
        const contact = c.contacto_id ? contactMap[c.contacto_id] : null
        const contactName = contact ? `${contact.nombre} ${contact.apellido || ''}`.toLowerCase() : ''
        const displayName = originNameMap[`${c.canal}:${c.channel_id || 'default'}`]?.toLowerCase() || ''
        if (!chatId.includes(q) && !preview.includes(q) && !contactName.includes(q) && !displayName.includes(q)) return false
      }
      return true
    })
  }, [conversations, filter, searchQuery, contactMap, originNameMap])

  const channelFiltered = useMemo(() => {
    if (!activeCanal && !activeOrigen) return sidebarConversations
    return sidebarConversations.filter(c => {
      if (activeCanal && c.canal !== activeCanal) return false
      if (activeOrigen && (c.channel_id || 'default') !== activeOrigen) return false
      return true
    })
  }, [sidebarConversations, activeCanal, activeOrigen])

  const orphanedChannel = canalParam && !activeCanal && !loading

  const hasFilteredChannel = canalParam && activeCanal && !loading
  const channelHasConvs = canalParam && channelFiltered.length > 0 && !loading

  return (
    <>
    <div className="page-enter chat-page">
      <div className={`chat-sidebar ${isMobile && selectedId !== null ? 'chat-sidebar--hidden' : ''}`}>
        <ChatSidebar
          conversations={sidebarConversations}
          contactMap={contactMap}
          originNameMap={originNameMap}
          selectedId={selectedId}
          onSelect={handleSelect}
          onSearch={setSearchQuery}
          filter={filter}
          onFilterChange={setFilter}
          loading={loading}
          onNewConversation={() => setShowNewConvModal(true)}
          onViewContact={(id) => setContactDetailId(id)}
        />
      </div>

      <div className={`chat-window ${isMobile && selectedId === null ? 'chat-window--hidden' : ''}`}>
        {selectedId ? (() => {
          const conv = conversations.find(c => c.id === selectedId)
          return (
            <ChatWindow
              conversacionId={selectedId}
              contactoId={conv?.contacto_id}
              canal={conv?.canal}
              onBack={isMobile ? handleBack : undefined}
              conversation={conv ?? null}
              contactMap={contactMap}
              onContactUpdated={(updated) => {
                actualizar(prev => ({ ...prev, contactMap: { ...prev.contactMap, [updated.id]: updated as Contacto } }))
              }}
            />
          )
        })() : hasFilteredChannel && !channelHasConvs ? (
          <div className="chat-window__empty">
            <IconSettings size={48} />
            <h3>Canal no configurado</h3>
            <p>
              El canal <strong>{canalParam}</strong> con origen <strong>{origenParam}</strong> no tiene conversaciones vinculadas.
            </p>
            <Link to="/configuracion" className="btn btn-primary" style={{ marginTop: 12 }}>
              <IconSettings size={16} /> Configurar Canales
            </Link>
          </div>
        ) : orphanedChannel ? (
          <div className="chat-window__empty">
            <IconSettings size={48} />
            <h3>Canal desconocido</h3>
            <p>El canal <strong>{canalParam}</strong> no es válido. Canales disponibles: whatsapp, instagram, webchat, email, sms, portal.</p>
            <Link to="/conversaciones" className="btn btn-secondary" style={{ marginTop: 12 }}>
              Ver todas las conversaciones
            </Link>
          </div>
        ) : (
          <div className="chat-window__empty">
            <IconMessageCircle size={64} />
            <h3>Selecciona una conversacion</h3>
            <p>Elige una conversacion del panel izquierdo para comenzar</p>
          </div>
        )}
      </div>
    </div>

    {showNewConvModal && (
      <NewConversationModal
        conversations={conversations}
        onStarted={handleConversationStarted}
        onClose={() => setShowNewConvModal(false)}
      />
    )}

    {contactDetailId && contactMap[contactDetailId] && (
      <div className="modal-backdrop" onClick={() => setContactDetailId(null)}>
        <div className="modal new-conv-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Contacto</h2>
            <button className="modal-close" onClick={() => setContactDetailId(null)} type="button">&times;</button>
          </div>
          <div className="modal-body">
            {(() => {
              const ct = contactMap[contactDetailId]
              return (
                <div className="contact-detail-card">
                  <div className="contact-detail-card__avatar">
                    {ct.nombre.charAt(0).toUpperCase()}
                  </div>
                  <h3 className="contact-detail-card__name">
                    {ct.nombre}{ct.apellido ? ` ${ct.apellido}` : ''}
                  </h3>
                  {ct.cargo && <span className="contact-detail-card__role">{ct.cargo}</span>}
                  <div className="contact-detail-card__fields">
                    {ct.telefono && (
                      <div className="contact-detail-card__field">
                        <span className="contact-detail-card__label">Telefono</span>
                        <span className="contact-detail-card__value">{ct.telefono}</span>
                      </div>
                    )}
                    {ct.email && (
                      <div className="contact-detail-card__field">
                        <span className="contact-detail-card__label">Email</span>
                        <span className="contact-detail-card__value">{ct.email}</span>
                      </div>
                    )}
                    {ct.estado_pipeline && (
                      <div className="contact-detail-card__field">
                        <span className="contact-detail-card__label">Pipeline</span>
                        <span className="contact-detail-card__value" style={{ textTransform: 'capitalize' }}>{ct.estado_pipeline}</span>
                      </div>
                    )}
                    {ct.origen_lead && (
                      <div className="contact-detail-card__field">
                        <span className="contact-detail-card__label">Origen</span>
                        <span className="contact-detail-card__value" style={{ textTransform: 'capitalize' }}>{ct.origen_lead}</span>
                      </div>
                    )}
                    {ct.notas && (
                      <div className="contact-detail-card__field">
                        <span className="contact-detail-card__label">Notas</span>
                        <span className="contact-detail-card__value">{ct.notas}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setContactDetailId(null)} type="button">Cerrar</button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
